import { HolderOutlined, PictureOutlined } from '@ant-design/icons';
import { Button, Flex, Tag, Typography, theme } from 'antd';
import type { HTMLAttributes, Ref } from 'react';

export interface SlotCardDragHandleProps extends HTMLAttributes<HTMLElement> {
  ref?: Ref<HTMLElement>;
}

export interface SlotCardProps {
  position: number;
  /** Banner title/campaign code. Omit (with isEmpty=true) to render the empty state. */
  title?: string;
  /** Already-resolved image src (CDN URL etc.) — this component does no URL transform. */
  imageSrc?: string;
  isEmpty: boolean;
  /** Ignored when isEmpty. true -> "Live" tag, false -> "Scheduled" tag. */
  isLive?: boolean;
  onAssign: () => void;
  onClear: () => void;
  isDragging?: boolean;
  dragHandleProps?: SlotCardDragHandleProps;
}

// Shared by both the Browser and Point carousel-slot admin pages for a consistent look —
// module isolation means each page still owns its own data shapes and wires them into
// this dumb, presentational component separately.
export const SlotCard = ({
  position,
  title,
  imageSrc,
  isEmpty,
  isLive,
  onAssign,
  onClear,
  isDragging,
  dragHandleProps,
}: SlotCardProps) => {
  const { token } = theme.useToken();
  const stateTag = isEmpty ? (
    <Tag>Empty</Tag>
  ) : isLive ? (
    <Tag color="success">Live</Tag>
  ) : (
    <Tag color="warning">Scheduled</Tag>
  );

  return (
    <div
      style={{
        border: `1px solid ${isDragging ? token.colorPrimary : token.colorBorderSecondary}`,
        borderRadius: 12,
        background: token.colorBgContainer,
        display: 'flex',
        flexDirection: 'column',
        // Fill the grid cell's stretched height (CSS Grid's default align-items:stretch
        // sizes every item in a row to the row's tallest natural content, e.g. a Scheduled
        // card's extra caption line) instead of a fixed px value, which would either clip
        // or overflow that caption on any card shorter than the tallest one in its row.
        height: '100%',
        minHeight: 200,
        cursor: isDragging ? 'grabbing' : undefined,
      }}
    >
      <Flex
        align="center"
        justify="space-between"
        style={{ padding: '9px 11px', borderBottom: `1px solid ${token.colorBorderSecondary}` }}
      >
        <Flex align="center" gap={7}>
          <span {...dragHandleProps} style={{ display: 'inline-flex', cursor: 'grab', touchAction: 'none' }}>
            <HolderOutlined style={{ color: token.colorTextSecondary }} />
          </span>
          <Typography.Text strong style={{ fontSize: 12 }}>
            Slot {position}
          </Typography.Text>
        </Flex>
        {stateTag}
      </Flex>

      <div style={{ padding: 11, display: 'flex', flexDirection: 'column', gap: 9, flex: 1, minHeight: 0 }}>
        {!isEmpty ? (
          <>
            <div
              style={{
                aspectRatio: '2/1',
                borderRadius: 9,
                overflow: 'hidden',
                background: token.colorFillAlter,
              }}
            >
              {imageSrc && (
                <img
                  src={imageSrc}
                  alt={title}
                  draggable={false}
                  style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                />
              )}
            </div>
            <Typography.Text style={{ fontSize: 12.5, fontWeight: 700 }} ellipsis={{ tooltip: title }}>
              {title || 'Untitled'}
            </Typography.Text>
            {!isLive && (
              <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                Scheduled - will appear once its start time passes
              </Typography.Text>
            )}
            <Flex gap={6} style={{ marginTop: 'auto' }}>
              <Button size="small" style={{ flex: 1 }} onClick={onAssign}>
                Change
              </Button>
              <Button size="small" style={{ flex: 1 }} onClick={onClear}>
                Clear
              </Button>
            </Flex>
          </>
        ) : (
          <>
            <Flex
              vertical
              align="center"
              justify="center"
              gap={8}
              style={{ flex: 1, padding: '14px 8px', color: token.colorTextTertiary, textAlign: 'center' }}
            >
              <PictureOutlined style={{ fontSize: 26 }} />
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                No banner assigned
              </Typography.Text>
            </Flex>
            <Button size="small" block onClick={onAssign}>
              Assign
            </Button>
          </>
        )}
      </div>
    </div>
  );
};

export default SlotCard;
